import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PageHeader, Badge, Btn, Table, FilterBar, clean } from "../../components/admin/AdminUI";
import { useAdmin, can } from "../../components/admin/AdminLayout";

const MemberSheet = ({ id, onClose, onChanged }) => {
  const admin = useAdmin();
  const [d, setD] = useState(null);
  const load = () => api.get(`/admin/members/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (id) load(); else setD(null); }, [id]); // eslint-disable-line
  const del = async () => {
    if (!window.confirm("Permanently anonymize and delete this member's personal data?")) return;
    try { await api.delete(`/admin/members/${id}`); toast.success("Member deleted"); onClose(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setReward = async (rid, v) => {
    try { await api.post(`/admin/rewards/${rid}/status`, { redemption_status: v }); toast.success("Reward updated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const m = d?.member;
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-[#0B0E0B] border-[#1E281E] w-full sm:max-w-xl overflow-y-auto" data-testid="member-detail-sheet">
        {m && <>
          <SheetHeader><SheetTitle className="font-display text-2xl uppercase">#{m.member_no} · {m.full_name}</SheetTitle></SheetHeader>
          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            {[["Email", m.email], ["Phone", m.phone], ["Age", `${m.age}${m.is_minor ? " (minor)" : ""}`], ["Source", m.referral_source + (m.referral_source_other ? ` — ${m.referral_source_other}` : "")],
              ["Status", <Badge s={m.status} />], ["Registered", fmtDateTime(m.created_at)], ["Referral code", m.referral_code || "—"], ["Eligible referrals", m.eligible_referrals],
              ["Reward tier", m.reward_tier || "None"], ["Marketing consent", m.marketing_consent ? "Yes" : "No"]].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-[#1E281E] p-3"><p className="text-[11px] uppercase text-[#7C8A7C]">{k}</p><div className="mt-1 break-all">{v}</div></div>
            ))}
          </div>
          <p className="eyebrow mt-7 mb-2">Rewards</p>
          {d.rewards.length === 0 ? <p className="text-sm text-[#7C8A7C]">None</p> : d.rewards.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 py-2 border-b border-[#1E281E] text-sm">
              <span className="capitalize">{r.tier} · {fmtDate(r.unlocked_at)}</span>
              {can(admin, "manage_members") ? (
                <Select value={r.redemption_status} onValueChange={(v) => setReward(r.id, v)}>
                  <SelectTrigger className="w-[200px] h-9 bg-[#080A08] border-[#2A362A]" data-testid={`reward-status-${r.tier}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{["awaiting_launch", "pending_provisioning", "provisioned", "redeemed", "superseded", "revoked"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>) : <Badge s={r.redemption_status} />}
            </div>))}
          <p className="eyebrow mt-7 mb-2">Referral history ({d.referrals.length})</p>
          <div className="max-h-64 overflow-y-auto">{d.referrals.map((r) => (
            <div key={r.id} className="flex justify-between py-2 border-b border-[#1E281E] text-sm"><span>#{r.referred_member_no} {r.referred_name || ""}</span><span className="flex gap-2 items-center"><Badge s={r.status} /><span className="text-xs text-[#7C8A7C]">{fmtDate(r.created_at)}</span></span></div>))}
          </div>
          <p className="eyebrow mt-7 mb-2">Consent records</p>
          {d.consents.map((c) => <p key={c.id} className="text-xs text-[#A3ADA3] py-1">{fmtDateTime(c.created_at)} · {c.status} · {c.source} · v{c.policy_version}</p>)}
          {can(admin, "manage_members") && <Btn variant="danger" className="mt-8" onClick={del} data-testid="delete-member-btn">Delete member data</Btn>}
        </>}
      </SheetContent>
    </Sheet>
  );
};

export default function AdminWaitlist() {
  const [f, setF] = useState({});
  const [page, setPage] = useState(1);
  const [d, setD] = useState({ items: [], total: 0 });
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/members", { params: { ...clean(f), page, page_size: 25 } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [f, page]); // eslint-disable-line
  const pages = Math.max(1, Math.ceil(d.total / 25));
  return (
    <div data-testid="admin-waitlist">
      <PageHeader title="Waitlist" subtitle={`${d.total} matching registrations`} />
      <FilterBar f={f} set={(v) => { setF(v); setPage(1); }} />
      <div className="mt-5">
        <Table testid="members-table" rows={d.items} onRow={(r) => setSel(r.id)} cols={[
          ["member_no", "ID"], ["full_name", "Name"], ["email", "Email"], ["phone", "Phone"], ["age", "Age"], ["referral_source", "Source"],
          ["created_at", "Registered", (r) => fmtDate(r.created_at)], ["status", "Verification", (r) => <Badge s={r.status} />],
          ["eligible_referrals", "Referrals"], ["reward_tier", "Tier", (r) => <span className="capitalize">{r.reward_tier || "—"}</span>],
          ["marketing_consent", "Consent", (r) => (r.marketing_consent ? "Yes" : "No")]]} />
      </div>
      <div className="mt-4 flex items-center justify-end gap-3 text-sm">
        <Btn variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev">Prev</Btn>
        <span data-testid="page-indicator">Page {page} / {pages}</span>
        <Btn variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="page-next">Next</Btn>
      </div>
      <MemberSheet id={sel} onClose={() => setSel(null)} onChanged={load} />
    </div>
  );
}
